import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/faro-dialog";
import { EmptyPage } from "@components/common/empty-page/empty-page";
import { MembersEmptyPage } from "@components/common/empty-page/members-empty-page";
import { MembersTable } from "@components/table/members/members-table";
import { MemberHeaders } from "@components/table/members/members-table-utils";
import FailedIcon from "@assets/icons/failed_32px.svg?react";
import { Box } from "@mui/material";
import { useAppParams } from "@router/router-helper";
import {
  activeMembersSelector,
  fetchingActiveMembersFlagsSelector,
} from "@store/active-members/active-members-selector";
import { useAppSelector } from "@store/store-helper";
import { useMembersUtils } from "@hooks/use-member-utils";
import { HIDE_ON_HOVER_CLASS } from "@utils/ui-utils";

/** Listing all the active members counted in current plan in a table */
export function ActiveMembersTable(): JSX.Element {
  const { companyId } = useAppParams();
  const activeMembers = useAppSelector(activeMembersSelector);
  const { status } = useAppSelector(fetchingActiveMembersFlagsSelector);
  const { openProfilePage } = useMembersUtils();

  // Show failed screen if request for fetching active members is failed
  if (status === "rejected") {
    return (
      <EmptyPage
        title="Error"
        subtitle={
          "Failed to fetch the active members. Please reload the page and try again."
        }
        icon={FailedIcon}
      />
    );
  }

  // Show empty screen if request for fetching active members succeeded but there is no active member
  if (status === "succeeded" && activeMembers.length === 0) {
    return (
      <MembersEmptyPage
        subtitle="All of your active members will be found here that are counted in the current plan."
        button={null}
      />
    );
  }

  return (
    <Box
      sx={{
        marginTop: SPACE_ELEMENTS_OF_MODAL,
        overflow: "auto",
      }}
    >
      <MembersTable
        companyId={companyId}
        members={activeMembers}
        tableSubject="activeMembers"
        shouldHideCheckbox={true}
        isLoading={status === "pending"}
        requiredColumns={[
          MemberHeaders.avatar,
          MemberHeaders.user,
          MemberHeaders.email,
          MemberHeaders.status,
          MemberHeaders.role,
          MemberHeaders.options,
        ]}
        onRowClick={(memberId) =>
          openProfilePage({
            userIdentity: memberId,
            source: "active members table",
          })
        }
        sx={{
          "& .MuiDataGrid-row": {
            cursor: "pointer",
          },

          [`& .MuiDataGrid-row:hover .${HIDE_ON_HOVER_CLASS}`]: {
            display: "none",
          },
        }}
      />
    </Box>
  );
}
